// ================================================
// DELEGATION TRACKER PANEL
// Feature 6 - Track delegated executive items
// ================================================
import React, { useState, useMemo } from 'react';
import { 
  HandMetal, Plus, Send, AlertTriangle, CheckCircle2,
  Clock, TrendingUp, ChevronDown, BarChart3, ArrowUp
} from 'lucide-react';
import { CHIEF_OF_STAFF_COLORS } from '../focus';
import { delegations, executiveTeam, formatDate, getDaysUntil } from '../../data/chiefOfStaffData';

const DelegationPanel = () => {
  const [items, setItems] = useState(delegations);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDelegation, setNewDelegation] = useState({ description: '', assigneeId: '', dueDate: '', source: '' });
  const [showPatterns, setShowPatterns] = useState(false);

  // Group by status
  const groupedItems = useMemo(() => {
    return {
      overdue: items.filter(i => i.status === 'overdue'),
      in_progress: items.filter(i => i.status === 'in_progress'),
      completed: items.filter(i => i.status === 'completed')
    };
  }, [items]);

  // Calculate patterns
  const patterns = useMemo(() => {
    const byAssignee = {};
    const byType = {};
    
    items.forEach(d => {
      const name = d.assignee.name;
      byAssignee[name] = (byAssignee[name] || 0) + 1;
    });
    
    // Calculate average completion time for completed items
    const completedItems = items.filter(i => i.status === 'completed' && i.completedDate);
    const avgCompletionDays = completedItems.length > 0
      ? completedItems.reduce((acc, item) => {
          const days = Math.floor((new Date(item.completedDate) - new Date(item.delegatedDate)) / (1000 * 60 * 60 * 24));
          return acc + days;
        }, 0) / completedItems.length
      : 0;
    
    return {
      byAssignee: Object.entries(byAssignee).sort((a, b) => b[1] - a[1]),
      avgCompletionDays: Math.round(avgCompletionDays),
      totalActive: items.filter(i => i.status !== 'completed').length,
      completionRate: Math.round((items.filter(i => i.status === 'completed').length / items.length) * 100)
    };
  }, [items]);

  const handleAddDelegation = () => {
    if (!newDelegation.description || !newDelegation.assigneeId) return;
    
    const assignee = executiveTeam.find(m => m.id === newDelegation.assigneeId);
    const newItem = {
      id: `del-${Date.now()}`,
      description: newDelegation.description,
      assignee: assignee,
      delegatedDate: new Date(),
      dueDate: newDelegation.dueDate ? new Date(newDelegation.dueDate) : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      status: 'in_progress',
      source: newDelegation.source || 'Direct delegation by Chief of Staff'
    };
    
    setItems([newItem, ...items]);
    setNewDelegation({ description: '', assigneeId: '', dueDate: '', source: '' });
    setShowAddForm(false);
    
    // Simulate notification
    alert(`Delegation created!\n${assignee.name} will be notified that "${newItem.description}" has been delegated to them.`);
  };

  const handleFollowUp = (item) => {
    const message = `Hi ${item.assignee.name.split(' ')[0]},\n\nFollowing up on the task delegated to you: "${item.description}"\n\nThis was delegated on ${formatDate(item.delegatedDate)} with a due date of ${formatDate(item.dueDate)}.\n\nCould you please provide a status update?\n\nThank you`;
    alert(`Follow-up message:\n\n${message}`);
  };

  const handleEscalate = (item) => {
    alert(`Escalating "${item.description}" to executive attention.\n\nThis item is ${getDaysUntil(item.dueDate).toLowerCase()} and requires immediate attention.`);
  };

  const statusConfig = {
    overdue: { label: 'Overdue', color: '#EF4444', bgColor: '#EF444420', icon: AlertTriangle },
    in_progress: { label: 'In Progress', color: CHIEF_OF_STAFF_COLORS.primary, bgColor: CHIEF_OF_STAFF_COLORS.primary + '20', icon: Clock },
    completed: { label: 'Completed This Week', color: '#10B981', bgColor: '#10B98120', icon: CheckCircle2 }
  };

  return (
    <div className="p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <HandMetal className="w-5 h-5" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
          <div>
            <h3 className="text-sm font-bold text-white">{patterns.totalActive} Active Delegations</h3>
            <p className="text-xs text-gray-400">
              {groupedItems.overdue.length} overdue • {patterns.completionRate}% completion rate
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowPatterns(!showPatterns)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all"
          style={{ 
            backgroundColor: showPatterns ? CHIEF_OF_STAFF_COLORS.primary : 'transparent',
            color: showPatterns ? 'white' : CHIEF_OF_STAFF_COLORS.primary,
            border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}`
          }}
        >
          <BarChart3 className="w-4 h-4" />
          Patterns
        </button>
      </div>

      {/* Patterns Panel */}
      {showPatterns && (
        <div 
          className="p-4 rounded-xl space-y-4"
          style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
        >
          <h4 className="text-xs font-bold text-gray-500 uppercase">Delegation Patterns</h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#0D1117' }}>
              <p className="text-2xl font-bold" style={{ color: CHIEF_OF_STAFF_COLORS.primary }}>
                {patterns.avgCompletionDays}
              </p>
              <p className="text-xs text-gray-400">Avg Days to Complete</p>
            </div>
            <div className="text-center p-3 rounded-lg" style={{ backgroundColor: '#0D1117' }}>
              <p className="text-2xl font-bold text-green-500">
                {patterns.completionRate}%
              </p>
              <p className="text-xs text-gray-400">Completion Rate</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <p className="text-xs text-gray-400">Delegations by Team Member</p>
            {patterns.byAssignee.slice(0, 5).map(([name, count]) => (
              <div key={name} className="flex items-center gap-2">
                <span className="text-xs text-white w-24 truncate">{name.split(' ')[0]}</span>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#0D1117' }}>
                  <div 
                    className="h-full rounded-full"
                    style={{ 
                      width: `${(count / items.length) * 100}%`,
                      backgroundColor: CHIEF_OF_STAFF_COLORS.primary
                    }}
                  />
                </div>
                <span className="text-xs text-gray-400 w-6">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Add */}
      {showAddForm ? (
        <div 
          className="p-4 rounded-xl space-y-3"
          style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}30` }}
        >
          <textarea
            placeholder="What are you delegating..."
            value={newDelegation.description}
            onChange={(e) => setNewDelegation({ ...newDelegation, description: e.target.value })}
            className="w-full p-3 rounded-lg text-sm text-white placeholder-gray-500 resize-none"
            style={{ backgroundColor: '#0D1117', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
            rows={2}
          />
          <div className="grid grid-cols-2 gap-2">
            <select
              value={newDelegation.assigneeId}
              onChange={(e) => setNewDelegation({ ...newDelegation, assigneeId: e.target.value })}
              className="p-3 rounded-lg text-sm text-white"
              style={{ backgroundColor: '#0D1117', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
            >
              <option value="">Delegate to...</option>
              {executiveTeam.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
            <input
              type="date"
              value={newDelegation.dueDate}
              onChange={(e) => setNewDelegation({ ...newDelegation, dueDate: e.target.value })}
              className="p-3 rounded-lg text-sm text-white"
              style={{ backgroundColor: '#0D1117', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
            />
          </div>
          <input
            type="text"
            placeholder="Source context (e.g., Monday sync, verbal)..."
            value={newDelegation.source}
            onChange={(e) => setNewDelegation({ ...newDelegation, source: e.target.value })}
            className="w-full p-3 rounded-lg text-sm text-white placeholder-gray-500"
            style={{ backgroundColor: '#0D1117', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddDelegation}
              className="flex-1 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary, color: 'white' }}
            >
              Delegate & Notify
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-white/10 text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all"
          style={{ backgroundColor: '#161B22', border: `1px dashed ${CHIEF_OF_STAFF_COLORS.primary}40`, color: CHIEF_OF_STAFF_COLORS.primary }}
        >
          <Plus className="w-4 h-4" />
          New Delegation
        </button>
      )}

      {/* Delegation Lists */}
      {Object.entries(groupedItems).map(([status, statusItems]) => {
        if (statusItems.length === 0) return null;
        const config = statusConfig[status];
        const StatusIcon = config.icon;
        
        return (
          <div key={status} className="space-y-2">
            <h3 
              className="text-xs font-bold uppercase tracking-wider flex items-center gap-2"
              style={{ color: config.color }}
            >
              <StatusIcon className="w-3 h-3" />
              {config.label} ({statusItems.length})
            </h3>
            
            {statusItems.map((item) => (
              <div 
                key={item.id}
                className="p-4 rounded-xl"
                style={{ 
                  backgroundColor: '#161B22',
                  borderLeft: `4px solid ${config.color}`
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-white mb-2">{item.description}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <div 
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                          style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary }}
                        >
                          {item.assignee.avatar}
                        </div>
                        {item.assignee.name}
                      </span>
                      <span>•</span>
                      <span>{getDaysUntil(item.dueDate)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{item.source}</p>
                  </div>
                  
                  {status !== 'completed' && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleFollowUp(item)}
                        className="flex items-center gap-1 px-2 py-1.5 rounded text-xs font-medium transition-all"
                        style={{ backgroundColor: config.bgColor, color: config.color }}
                      >
                        <Send className="w-3 h-3" />
                      </button>
                      {status === 'overdue' && (
                        <button
                          onClick={() => handleEscalate(item)}
                          className="flex items-center gap-1 px-2 py-1.5 rounded text-xs font-medium bg-red-500/20 text-red-500"
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                
                {item.status === 'completed' && item.completedDate && (
                  <div className="mt-2 pt-2 border-t border-white/10">
                    <span className="text-xs text-green-500 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Completed {formatDate(item.completedDate)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default DelegationPanel;
